
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LayoutDashboard } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Project } from '@/types/supabase';
import ProjectReviewList from '@/components/mentor/projects/ProjectReviewList';
import ProjectFeedbackForm from '@/components/mentor/projects/ProjectFeedbackForm';

// Extended Project type with joined user_profiles data
interface ProjectWithUserProfile extends Project {
  user_profiles?: {
    first_name: string | null;
    last_name: string | null;
  };
}

const MentorProjects = () => {
  const { isMentor } = useAuth();
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [activeTab, setActiveTab] = useState('pending');
  
  useEffect(() => {
    // Redirect if not a mentor
    if (!isMentor()) {
      navigate('/dashboard');
    }
  }, [isMentor, navigate]);

  // Fetch projects that need review
  const { data: pendingProjects, isLoading: isPendingLoading, refetch: refetchPending } = useQuery({
    queryKey: ['mentor-pending-projects'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select(`
            id,
            title,
            description,
            submission_url,
            feedback,
            score,
            created_at,
            updated_at,
            user_id
          `)
          .not('submission_url', 'is', null)
          .is('score', null)
          .order('updated_at', { ascending: false });
        
        if (error) throw error;
        
        // Fetch user profiles separately
        if (data && data.length > 0) {
          const projectsWithProfiles = await Promise.all(
            data.map(async (project) => {
              const { data: profileData } = await supabase
                .from('user_profiles')
                .select('first_name, last_name')
                .eq('id', project.user_id)
                .single();
                
              return {
                ...project,
                user_profiles: profileData || { first_name: null, last_name: null }
              } as ProjectWithUserProfile;
            })
          );
          
          return projectsWithProfiles;
        }
        
        return [];
      } catch (error) {
        console.error('Error fetching pending projects:', error);
        return [];
      }
    }
  });

  // Fetch projects that have been reviewed
  const { data: reviewedProjects, isLoading: isReviewedLoading, refetch: refetchReviewed } = useQuery({
    queryKey: ['mentor-reviewed-projects'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select(`
            id,
            title,
            description,
            submission_url,
            feedback,
            score,
            created_at,
            updated_at,
            user_id
          `)
          .not('submission_url', 'is', null)
          .not('score', 'is', null)
          .order('updated_at', { ascending: false });
        
        if (error) throw error;
        
        // Fetch user profiles separately
        if (data && data.length > 0) {
          const projectsWithProfiles = await Promise.all(
            data.map(async (project) => {
              const { data: profileData } = await supabase
                .from('user_profiles')
                .select('first_name, last_name')
                .eq('id', project.user_id)
                .single();
                
              return {
                ...project,
                user_profiles: profileData || { first_name: null, last_name: null }
              } as ProjectWithUserProfile;
            })
          );
          
          return projectsWithProfiles;
        }
        
        return [];
      } catch (error) {
        console.error('Error fetching reviewed projects:', error);
        return [];
      }
    }
  });

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
  };

  const handleFeedbackSubmit = async () => {
    // Refetch both lists after feedback submission
    await refetchPending();
    await refetchReviewed();
    
    // If we were viewing a pending project that's now reviewed, switch tabs
    if (activeTab === 'pending' && selectedProject) {
      setActiveTab('reviewed');
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight section-header">Project Reviews</h1>
          <p className="text-muted-foreground section-subheader">Review and provide feedback on student projects</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/mentor')}>
          <LayoutDashboard className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Projects</CardTitle>
              <CardDescription>
                Select a project to review
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs defaultValue="pending" value={activeTab} onValueChange={setActiveTab}>
                <div className="px-6 pb-3">
                  <TabsList className="w-full">
                    <TabsTrigger value="pending" className="flex-1">
                      Pending Review
                      {pendingProjects && pendingProjects.length > 0 && (
                        <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                          {pendingProjects.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="reviewed" className="flex-1">
                      Reviewed
                    </TabsTrigger>
                  </TabsList>
                </div>
                
                <TabsContent value="pending" className="m-0">
                  <ProjectReviewList 
                    projects={pendingProjects || []} 
                    isLoading={isPendingLoading}
                    selectedProjectId={selectedProject?.id}
                    onSelectProject={handleProjectSelect}
                    emptyMessage="No projects waiting for review"
                  />
                </TabsContent>
                
                <TabsContent value="reviewed" className="m-0">
                  <ProjectReviewList 
                    projects={reviewedProjects || []} 
                    isLoading={isReviewedLoading}
                    selectedProjectId={selectedProject?.id}
                    onSelectProject={handleProjectSelect}
                    emptyMessage="No reviewed projects yet"
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
        
        <div className="lg:col-span-2">
          {selectedProject ? (
            <ProjectFeedbackForm 
              project={selectedProject} 
              onFeedbackSubmitted={handleFeedbackSubmit}
            />
          ) : (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="text-center py-10">
                <p className="text-muted-foreground">
                  Select a project from the list to review
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default MentorProjects;
